import {createRef, useMemo} from 'react'

interface UseCodeInputProps {
    value: string
    length: number
    onChange: (code: string) => void
    onSubmit: (code: string) => void
}

const emptyDigit = ' '

const useCodeInput = ({
    onSubmit,
    onChange,
    length,
    value,
}: UseCodeInputProps) => {
    const fillValues = (value: string) =>
        new Array(length).fill('').map((_, index) => value[index] ?? '')
    const values = fillValues(value)

    const inputRefs = useMemo(
        () =>
            new Array(length)
                .fill(null)
                .map(() => createRef<HTMLInputElement>()),
        [length],
    )

    const focusInput = (idx: number) => {
        const input = inputRefs[idx] && inputRefs[idx].current
        if (input) {
            return input.focus()
        }
    }

    const onChangeInput =
        (idx: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
            let numberValue = parseInt(e.target.value)
            if (numberValue >= 10) {
                numberValue = numberValue % 10
            }
            const newValueDigit = !isNaN(numberValue) ? numberValue : emptyDigit

            const newCode = [...values]
            newCode[idx] = newValueDigit.toString()
            onChange(newCode.join(''))
            if (newValueDigit === emptyDigit) {
                return
            }

            focusInput(idx + 1)
            if (
                newCode.length === length &&
                !newCode.includes(emptyDigit) &&
                !newCode.includes('')
            ) {
                const code = newCode.join('')
                code && onSubmit(code)
            }
        }

    const onKeyDownInput = ({
        e,
        idx,
    }: {
        e: React.KeyboardEvent<HTMLInputElement>
        idx: number
    }) => {
        const key = e.key
        if (key === 'Backspace') {
            e.preventDefault()
            const newCode = [...values]
            if (newCode[idx] === emptyDigit) {
                focusInput(idx - 1)
            }
            newCode[idx] = emptyDigit
            onChange(newCode.join(''))
        }
    }

    const onInputPaste =
        () => (event: React.ClipboardEvent<HTMLInputElement>) => {
            event.preventDefault()

            const pastedValue = event.clipboardData.getData('text')
            const nextValues = pastedValue.slice(0, length)

            const newCode = fillValues(nextValues)
            onChange(newCode.join(''))

            if (
                newCode.length === length &&
                !newCode.includes(emptyDigit) &&
                !newCode.includes('')
            ) {
                const code = newCode.join('')
                code && onSubmit(code)
                return
            }

            focusInput(pastedValue.length)
        }

    return {
        values,
        inputsInOrder: inputRefs,
        emptyDigit,
        onChangeInput,
        onKeyDownInput,
        onInputPaste,
    }
}

export default useCodeInput
